import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AnswerCondition,
  ConditionOperator,
  extractConditionQuestions,
  getDefaultOperator,
  getInputType,
  getOperatorsForType,
} from "@/lib/form-conditions";
import type { ConditionGroup } from "@/types/process-trigger.types";

export interface TriggerStep {
  formId: string;
  name: string;
  order: number;
  design: unknown;
}

interface Props {
  // Only the steps whose answers exist when the trigger fires.
  steps: TriggerStep[];
  value: ConditionGroup;
  onChange: (value: ConditionGroup) => void;
}

const emptyCondition = (formId = ""): AnswerCondition => ({
  formId,
  questionId: "",
  operator: "equals",
  expectedValue: "",
});

const ConditionRow: React.FC<{
  steps: TriggerStep[];
  condition: AnswerCondition;
  onChange: (condition: AnswerCondition) => void;
  onRemove?: () => void;
}> = ({ steps, condition, onChange, onRemove }) => {
  const { t } = useTranslation();
  const step = steps.find((s) => s.formId === condition.formId);
  const questions = step ? extractConditionQuestions(step.design) : [];
  const question = questions.find((q) => q.id === condition.questionId);
  const operators = question ? getOperatorsForType(question.type) : [];

  const renderValue = () => {
    if (!question) return <Input disabled placeholder={t("processTriggers.conditions.value")} />;
    if ((question.type === "Dropdown" || question.type === "Checkbox") && question.options?.length) {
      return (
        <Select
          value={String(condition.expectedValue ?? "")}
          onValueChange={(expectedValue) => onChange({ ...condition, expectedValue })}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("processTriggers.conditions.value")} />
          </SelectTrigger>
          <SelectContent>
            {question.options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    const inputType = getInputType(question.type);
    return (
      <Input
        type={inputType}
        value={condition.expectedValue ?? ""}
        placeholder={t("processTriggers.conditions.value")}
        onChange={(e) =>
          onChange({
            ...condition,
            expectedValue: inputType === "number" ? Number(e.target.value) : e.target.value,
          })
        }
      />
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px_1fr_auto] gap-2 items-center">
      <Select
        value={condition.formId}
        onValueChange={(formId) => onChange(emptyCondition(formId))}
      >
        <SelectTrigger>
          <SelectValue placeholder={t("processTriggers.conditions.form")} />
        </SelectTrigger>
        <SelectContent>
          {steps.map((s) => (
            <SelectItem key={s.formId} value={s.formId}>
              {s.order}. {s.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={condition.questionId}
        disabled={!step}
        onValueChange={(questionId) => {
          const q = questions.find((x) => x.id === questionId);
          onChange({
            ...condition,
            questionId,
            operator: q ? getDefaultOperator(q.type) : "equals",
            expectedValue: "",
          });
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder={t("processTriggers.conditions.question")} />
        </SelectTrigger>
        <SelectContent>
          {questions.map((q) => (
            <SelectItem key={q.id} value={q.id}>
              {q.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={condition.operator}
        disabled={!question}
        onValueChange={(operator) =>
          onChange({ ...condition, operator: operator as ConditionOperator, expectedValue: "" })
        }
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operators.map((op) => (
            <SelectItem key={op} value={op}>
              {t(`certificate.approvalCondition.operators.${op}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {renderValue()}

      <button
        type="button"
        onClick={onRemove}
        disabled={!onRemove}
        className="p-2 text-gray-500 hover:text-red-600 disabled:opacity-30"
        aria-label={t("processTriggers.conditions.remove")}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
};

const ConditionGroupBuilder: React.FC<Props> = ({ steps, value, onChange }) => {
  const { t } = useTranslation();
  const conditions = value.conditions.length ? value.conditions : [emptyCondition()];

  const update = (index: number, condition: AnswerCondition) =>
    onChange({ ...value, conditions: conditions.map((c, i) => (i === index ? condition : c)) });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <Label>{t("processTriggers.conditions.startWhen")}</Label>
        <Select
          value={value.match}
          onValueChange={(match) => onChange({ ...value, match: match as ConditionGroup["match"] })}
        >
          <SelectTrigger className="w-28 h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t("processTriggers.conditions.all")}</SelectItem>
            <SelectItem value="ANY">{t("processTriggers.conditions.any")}</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-gray-600">{t("processTriggers.conditions.ofTheseMatch")}</span>
      </div>

      {conditions.map((condition, index) => (
        <ConditionRow
          key={index}
          steps={steps}
          condition={condition}
          onChange={(c) => update(index, c)}
          onRemove={
            conditions.length > 1
              ? () => onChange({ ...value, conditions: conditions.filter((_, i) => i !== index) })
              : undefined
          }
        />
      ))}

      <button
        type="button"
        onClick={() => onChange({ ...value, conditions: [...conditions, emptyCondition()] })}
        className="flex items-center gap-1 text-sm text-darkBlue hover:underline"
      >
        <Plus className="h-4 w-4" /> {t("processTriggers.conditions.add")}
      </button>
    </div>
  );
};

export default ConditionGroupBuilder;
